import React, { useEffect, useState, useCallback } from "react";
import { CalendarDays, Save, Plus, Trash2, Clock, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Concierge Dashboard — Availability panel.
 *
 * Lets a Concierge set:
 *   • Weekly recurring schedule (per-day list of {start,end} HH:MM slots)
 *   • Blackout calendar dates (vacation / OOO / personal)
 *   • Minimum booking notice (hours)
 *   • Maximum advance booking window (days)
 *   • Whether to accept flexible-date requests
 *
 * Server-side endpoints:
 *   GET  /api/concierge/availability/me
 *   PUT  /api/concierge/availability/me
 *
 * The "Next available" indicator at the top of the panel uses the same
 * computation logic the customer sees on the public profile — so the
 * Concierge can sanity-check what bookings look like for them.
 */
const DAYS = [
  { key: "monday",    label: "Mon" },
  { key: "tuesday",   label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday",  label: "Thu" },
  { key: "friday",    label: "Fri" },
  { key: "saturday",  label: "Sat" },
  { key: "sunday",    label: "Sun" },
];

export default function ConciergeAvailabilityPanel() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlackout, setNewBlackout] = useState({ date: "", reason: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/concierge/availability/me")
      .then((r) => setDoc(r.data))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Could not load availability.";
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !doc) {
    return <div className="p-8 text-center text-[#071A2D]/60">Loading availability…</div>;
  }

  const updateDay = (day, slots) => {
    setDoc((d) => ({ ...d, weekly_schedule: { ...d.weekly_schedule, [day]: slots } }));
  };

  const addSlot = (day) => {
    const existing = doc.weekly_schedule[day] || [];
    updateDay(day, [...existing, { start: "17:00", end: "23:00" }]);
  };

  const removeSlot = (day, idx) => {
    const existing = doc.weekly_schedule[day] || [];
    updateDay(day, existing.filter((_, i) => i !== idx));
  };

  const updateSlot = (day, idx, key, value) => {
    const existing = doc.weekly_schedule[day] || [];
    updateDay(day, existing.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };

  const addBlackout = () => {
    if (!newBlackout.date) return toast.error("Pick a date first.");
    setDoc((d) => ({
      ...d,
      blackout_dates: [...(d.blackout_dates || []), { ...newBlackout }],
    }));
    setNewBlackout({ date: "", reason: "" });
  };

  const removeBlackout = (date) => {
    setDoc((d) => ({
      ...d,
      blackout_dates: (d.blackout_dates || []).filter((b) => b.date !== date),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/concierge/availability/me", {
        timezone: doc.timezone,
        weekly_schedule: doc.weekly_schedule,
        blackout_dates: doc.blackout_dates,
        notice_hours: Number(doc.notice_hours) || 48,
        max_advance_days: Number(doc.max_advance_days) || 90,
        accepts_flexible: !!doc.accepts_flexible,
        accepts_instant_book: !!doc.accepts_instant_book,
      });
      setDoc(r.data);
      toast.success("Availability saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save availability.");
    }
    setSaving(false);
  };

  return (
    <div data-testid="concierge-availability-panel" className="space-y-6">
      {/* Header + next-available */}
      <div className="p-5 rounded-2xl bg-[#071A2D] text-[#FFF8EE] border border-[#C9A45C]/30">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold flex items-center gap-1.5">
          <CalendarDays size={12}/> Availability
        </div>
        <div className="mt-2 font-serif text-2xl">When customers can book you</div>
        <p className="text-xs text-[#FFF8EE]/75 mt-1.5">
          Set your weekly schedule, blackout dates, and how far out you accept bookings. Customers see &ldquo;Next available&rdquo; on your profile.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A45C] text-[#071A2D] text-xs font-semibold" data-testid="next-available-pill">
          <Clock size={12}/> Next available: {doc.next_available_date || "—"}
        </div>
      </div>

      {/* Weekly schedule */}
      <div className="rounded-2xl card-luxury p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Weekly schedule</div>
        <div className="text-sm text-[#071A2D]/70 mt-1">Tap a day to add or remove time blocks. Empty days mean no bookings that day.</div>
        <div className="mt-4 space-y-3">
          {DAYS.map((d) => {
            const slots = doc.weekly_schedule?.[d.key] || [];
            return (
              <div key={d.key} className="flex items-start gap-3 flex-wrap">
                <div className="w-12 text-sm font-semibold text-[#064E3B]">{d.label}</div>
                <div className="flex-1 flex flex-wrap gap-2 items-center">
                  {slots.length === 0 && (
                    <span className="text-xs italic text-[#071A2D]/45">Off</span>
                  )}
                  {slots.map((s, i) => (
                    <div
                      key={`${d.key}-slot-${i}`}
                      className="flex items-center gap-1.5 bg-white rounded-full border border-[#071A2D]/10 px-2.5 py-1"
                    >
                      <input
                        data-testid={`avail-${d.key}-start-${i}`}
                        type="time"
                        value={s.start}
                        onChange={(e) => updateSlot(d.key, i, "start", e.target.value)}
                        className="text-xs bg-transparent w-20 focus:outline-none"
                      />
                      <span className="text-xs text-[#071A2D]/45">→</span>
                      <input
                        data-testid={`avail-${d.key}-end-${i}`}
                        type="time"
                        value={s.end}
                        onChange={(e) => updateSlot(d.key, i, "end", e.target.value)}
                        className="text-xs bg-transparent w-20 focus:outline-none"
                      />
                      <button
                        data-testid={`avail-${d.key}-remove-${i}`}
                        onClick={() => removeSlot(d.key, i)}
                        className="text-[#FF7A45] hover:text-[#FF7A45]/70"
                        aria-label="Remove slot"
                      >
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid={`avail-${d.key}-add`}
                    onClick={() => addSlot(d.key)}
                    className="text-xs flex items-center gap-1 text-[#064E3B] underline"
                  >
                    <Plus size={12}/> Add time
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Booking rules */}
      <div className="rounded-2xl card-luxury p-5 grid md:grid-cols-2 gap-4">
        <Field label="Minimum notice (hours)" hint="How much heads-up you need before a booking starts.">
          <input
            data-testid="avail-notice-hours"
            type="number" min={0} max={168}
            value={doc.notice_hours ?? 48}
            onChange={(e) => setDoc((d) => ({ ...d, notice_hours: Number(e.target.value) }))}
            className="kbd-input"
          />
        </Field>
        <Field label="Max advance booking (days)" hint="How far in the future customers can book.">
          <input
            data-testid="avail-max-advance-days"
            type="number" min={1} max={365}
            value={doc.max_advance_days ?? 90}
            onChange={(e) => setDoc((d) => ({ ...d, max_advance_days: Number(e.target.value) }))}
            className="kbd-input"
          />
        </Field>
        <ToggleRow
          testid="avail-accepts-flexible"
          label="Accept flexible-date requests"
          hint='Lets customers send "my dates are flexible" — you confirm a date later.'
          checked={!!doc.accepts_flexible}
          onChange={(v) => setDoc((d) => ({ ...d, accepts_flexible: v }))}
        />
        <ToggleRow
          testid="avail-accepts-instant"
          label="Allow instant booking"
          hint="Skip the request step for verified customers (off by default)."
          checked={!!doc.accepts_instant_book}
          onChange={(v) => setDoc((d) => ({ ...d, accepts_instant_book: v }))}
        />
      </div>

      {/* Blackout dates */}
      <div className="rounded-2xl card-luxury p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Blackout dates</div>
        <div className="text-sm text-[#071A2D]/70 mt-1">Mark specific days as unavailable — vacation, family, anything.</div>

        <div className="mt-4 flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#064E3B]/70 block mb-1">Date</label>
            <input
              data-testid="avail-blackout-date"
              type="date"
              value={newBlackout.date}
              onChange={(e) => setNewBlackout({ ...newBlackout, date: e.target.value })}
              className="kbd-input"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-widest text-[#064E3B]/70 block mb-1">Reason (optional)</label>
            <input
              data-testid="avail-blackout-reason"
              value={newBlackout.reason}
              onChange={(e) => setNewBlackout({ ...newBlackout, reason: e.target.value })}
              placeholder="OOO, vacation, family…"
              className="kbd-input"
            />
          </div>
          <button
            data-testid="avail-blackout-add"
            onClick={addBlackout}
            className="btn-emerald text-xs px-4 py-2 flex items-center gap-1"
          >
            <Plus size={12}/> Add
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {(doc.blackout_dates || []).length === 0 && (
            <span className="text-xs italic text-[#071A2D]/45">No blackout dates set.</span>
          )}
          {(doc.blackout_dates || []).map((b) => (
            <span
              key={b.date}
              data-testid={`avail-blackout-${b.date}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#FF7A45]/12 text-[#FF7A45] border border-[#FF7A45]/25"
            >
              <AlertCircle size={11}/>
              {b.date}
              {b.reason ? <span className="opacity-70">· {b.reason}</span> : null}
              <button
                onClick={() => removeBlackout(b.date)}
                className="ml-1 text-[#FF7A45] hover:text-[#FF7A45]/70"
                aria-label="Remove blackout"
              >
                <Trash2 size={11}/>
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          data-testid="avail-save"
          onClick={save}
          disabled={saving}
          className="btn-coral inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Save size={14}/> {saving ? "Saving…" : "Save availability"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#064E3B]/70 block mb-1">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-[#071A2D]/55 mt-1">{hint}</div>}
  </div>
);

const ToggleRow = ({ label, hint, checked, onChange, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      data-testid={testid}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1.5 w-4 h-4 rounded border-[#064E3B]/40 text-[#064E3B] focus:ring-[#064E3B]"
    />
    <span>
      <span className="text-sm font-medium text-[#071A2D]">{label}</span>
      {hint && <span className="block text-[11px] text-[#071A2D]/55 mt-0.5">{hint}</span>}
    </span>
  </label>
);
